import { Button } from '@tunasong/ui-lib'
import { Typography } from '@tunasong/ui-lib'
import { Check, Fail } from '@tunasong/icons'
import { getRandomNote, GuitarLib, NoteLib } from '@tunasong/music-lib'
import { Fretboard } from '@tunasong/music-ui'
import type { NoteName, Persisted } from '@tunasong/schemas'
import { standardNoteNames } from '@tunasong/schemas'
import type { FC, JSX } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import ScorePanel from './score-panel.js'
import { useScorer } from './scorer.hook.js'
import type { FretNoteGame as FretNoteGameType } from '../types.js'
import { useGameResults } from './game-results.hook.js'

export interface FretNoteGameProps {
  /** The game */
  game: Persisted<FretNoteGameType>
}

export const FretNoteGame: FC<FretNoteGameProps> = props => {
  const { game } = props

  const [note, setNote] = useState(getRandomNote())
  const [icons, setIcons] = useState<Partial<Record<NoteName, JSX.Element | undefined>>>({})

  const fingering = useMemo(() => {
    const fingerings: GuitarLib.Fingering[] = []
    for (let str = 1; str <= 6; str++) {
      for (let fret = 1; fret < 12; fret++) {
        const fingering = { str: str as GuitarLib.StringNumber, fret }
        const n = GuitarLib.getNoteFromFingering(fingering)
        if (n && NoteLib.equals(n, note)) {
          fingerings.push(fingering)
        }
      }
    }
    const withMeta = GuitarLib.getFingeringWithMetadata(fingerings)
    /** return a random fingering */
    return withMeta[Math.floor(Math.random() * fingerings.length)]
  }, [note])

  const handleSaveScore = () => {
    
  }

  const userGameResults = useGameResults({ game })

  const { score, userStats, correct, fail } = useScorer({ userGameResults, saveScore: handleSaveScore })

  const timeout = useRef<number | NodeJS.Timeout>(null)
  const clearIcons = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }
    timeout.current = setTimeout(() => {
      setIcons({})
    }, 1000)
  }, [])

  const handleClick = useCallback(
    (n: NoteName) => () => {
      if (NoteLib.equals(n, note)) {
        setIcons(icons => ({ ...icons, [n]: <Check color="success" /> }))
        correct()
      } else {
        setIcons(icons => ({ ...icons, [n]: <Fail color="error" /> }))
        fail()
      }
      setNote(getRandomNote())
      clearIcons()
    },
    [clearIcons, correct, fail, note]
  )

  return (
    <div style={{ display: 'block' }}>
      <Fretboard fingerings={[fingering]} labels={'none'} />
      {userStats ? <ScorePanel score={score} userStats={userStats} game={game} /> : null}
      <Typography variant="caption">Click the correct note name</Typography>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr' }}>
        {standardNoteNames.map(n => (
          <Button variant="outlined" key={n} onClick={handleClick(n)} endIcon={icons[n]}>
            {n}
          </Button>
        ))}
      </div>
    </div>
  )
}

export default FretNoteGame
